import React from 'react';

import { Box, Grid, GridItem } from '@chakra-ui/react';
import { ParentOKR } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Overview/ParentOKR';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import { OBJECTIVE_LEVEL, OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';

import { Tooltip } from 'uiToolkitV1/Tooltip';

import { OKRCreationTimeline } from '../../AlignmentSection/OKRCreationTimeline';

interface Props {
  isAlignmentDisabled: boolean;
}

const Alignment = ({ isAlignmentDisabled }: Props) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const okrDetails = useSelector(okrCreationPanelStateSelector);

  const { timeBoundDetails, text, type, visibilityType, objectiveLevel } = okrDetails;
  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const parentPost = okrDetails?.parentPost ?? {
    uuid: null,
    text: 'None',
    type: OKR_TYPES.OBJECTIVE,
    visibilityType: 'PRIVATE',
  };

  const { canCreateKeyResultAtRootLevel } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const isParentOptional =
    type === OKR_TYPES.OBJECTIVE ? objectiveLevel === OBJECTIVE_LEVEL.COMPANY : canCreateKeyResultAtRootLevel;

  const isParentMandatory = !isParentOptional;

  const labelText = (
    <span>
      What{' '}
      {isObjective
        ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
        : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)}{' '}
      this {modifyString(moduleName, StringModifier.SENTENCE_CASE)} align to.
      {isParentMandatory ? <sup style={{ top: 0, fontSize: '14px' }}>*</sup> : ''}
    </span>
  );

  const gridTemplate = isMobileView()
    ? { gridTemplateRows: '44px 44px', rowGap: '16px' }
    : { gridTemplateColumns: '23% 77%', columnGap: '16px', h: '44px' };

  return (
    <Box w="100%" mb="16px">
      <Grid {...gridTemplate}>
        <GridItem display="flex">
          <Tooltip label="Timeline" shouldWrapChildren>
            <Box height="44px" display="flex" justifyContent="flex-end">
              <OKRCreationTimeline
                okrUuid={null}
                okrType={type}
                okrVisibilityType={visibilityType}
                okrText={text}
                objectiveLevel={objectiveLevel}
                okrHasNoChildren
                timeBoundDetails={timeBoundDetails}
                parentPost={parentPost}
                isReadOnly={isAlignmentDisabled}
                showLabel={false}
              />
            </Box>
          </Tooltip>
        </GridItem>

        <GridItem
          paddingRight="12px"
          overflow="hidden"
          sx={{
            '.parent-okr-component': {
              height: '44px',
            },
          }}
        >
          <Tooltip label="Parent goal alignment" shouldWrapChildren>
            <ParentOKR
              // @ts-ignore
              labelText={labelText}
              parentPost={parentPost}
              okrUuid={null}
              okrType={type}
              okrVisibilityType={visibilityType}
              okrText={text}
              objectiveLevel={objectiveLevel}
              okrHasNoChildren
              timeBoundDetails={timeBoundDetails}
              isReadOnly={isAlignmentDisabled}
              showLabel={false}
            />
          </Tooltip>
        </GridItem>
      </Grid>
    </Box>
  );
};

export { Alignment };
